import CloseIcon from "@mui/icons-material/Close";
import { Button, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { Dialog } from "@surfaces/Dialog";
import type { ReactNode } from "react";
import type { FieldValues } from "react-hook-form";
import { UnsavedChangesGuardContextProvider, useUnsavedChangesGuardSplitContext } from "./context";
import type { IUseUnsavedChangesGuardFormOptions } from "./form";

// === Modal Component (internal) ===
interface IUnsavedChangesGuardModalProps {
  title: string;
  message: string[];
  saveAndLeaveText: string;
  cancelText: string;
}

const UnsavedChangesGuardModal = ({ title, message, saveAndLeaveText, cancelText }: IUnsavedChangesGuardModalProps) => {
  const openConfirmationModal = useUnsavedChangesGuardSplitContext("openConfirmationModal");
  const saveLoading = useUnsavedChangesGuardSplitContext("saveLoading");
  const handleSaveAndLeave = useUnsavedChangesGuardSplitContext("handleSaveAndLeave");
  const onCancel = useUnsavedChangesGuardSplitContext("onCancel");
  const onClose = useUnsavedChangesGuardSplitContext("onClose");

  return (
    <Dialog open={openConfirmationModal}>
      <DialogTitle>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h4">{title}</Typography>
          <IconButton onClick={onClose} sx={{ mr: -1 }}>
            <CloseIcon sx={(theme) => ({ color: theme.palette.black.light })} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          {message.map((string) => (
            <Typography key={string}>{string}</Typography>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onCancel} disabled={saveLoading}>
          {cancelText}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveAndLeave}
          disabled={saveLoading}
          loading={saveLoading}
        >
          {saveAndLeaveText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export interface IUnsavedChangesGuardProps<T extends FieldValues = FieldValues> {
  children: ReactNode;
  form?: IUseUnsavedChangesGuardFormOptions<T>;
  title?: string;
  message?: string[];
  saveAndLeaveText?: string;
  cancelText?: string;
}

export const UnsavedChangesGuard = <T extends FieldValues = FieldValues>({
  children,
  form,
  title = "Enregistrer vos modifications avant de quitter",
  message = [
    "Vous venez d'effectuer des modifications sur cette page. Si vous quittez la page sans enregistrer, celles-ci seront perdues.",
    "Voulez-vous enregistrer les modifications avant de quitter cette page ?",
  ],
  saveAndLeaveText = "Oui, enregistrer et quitter",
  cancelText = "Non, ne pas enregistrer",
}: IUnsavedChangesGuardProps<T>) => {
  return (
    <UnsavedChangesGuardContextProvider form={form}>
      <UnsavedChangesGuardModal
        title={title}
        message={message}
        saveAndLeaveText={saveAndLeaveText}
        cancelText={cancelText}
      />
      {children}
    </UnsavedChangesGuardContextProvider>
  );
};
